// src/pages/ProductScreen.js
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useApp } from '../lib/AppContext';
import { t } from '../lib/i18n';
import { formatEurCheckout } from '../lib/currency';
import { useSEO, productSchema, breadcrumbSchema, organizationSchema } from '../lib/useSEO';

export default function ProductScreen() {
  const { id } = useParams();
  const { lang, fmt, addToCart, profile, user, showToast, refreshProfile } = useApp();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [qty, setQty] = useState(1);
  const [added, setAdded] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    supabase.from('products').select('*, category:categories(slug, name, icon)').eq('id', id).single()
      .then(({ data }) => { setProduct(data); setLoading(false); });
  }, [id]);

  const toggleWishlist = async () => {
    if (!user) { navigate('/login'); return; }
    const current = profile?.wishlist || [];
    const newList = current.includes(id) ? current.filter(x => x !== id) : [...current, id];
    await supabase.from('profiles').update({ wishlist: newList }).eq('id', user.id);
    await refreshProfile();
    showToast(current.includes(id) ? 'Removed from wishlist' : 'Added to wishlist', 'success');
  };

  const handleAdd = () => {
    for (let i = 0; i < qty; i++) addToCart(product);
    setAdded(true);
    setTimeout(() => setAdded(false), 1500);
  };

  const name    = product?.name?.[lang] || product?.name?.en || 'Product';
  const desc    = product?.description?.[lang] || product?.description?.en || '';
  const inWishlist = profile?.wishlist?.includes(id);
  const catName = product?.category?.name?.[lang] || product?.category?.name?.en || 'Collection';

  useSEO({
    title:       product ? name : undefined,
    description: product
      ? (desc ? `${desc.slice(0, 140).trimEnd()}…` : `${name} — premium botanical collector specimen. EU-compliant CBD collectible from CosmiqHerbz.`)
      : undefined,
    canonical:   `/product/${id}`,
    lang,
    og: {
      type:  'product',
      image: product?.images?.[0],
    },
    jsonLd: product ? [
      productSchema(product, lang),
      breadcrumbSchema([
        { name: 'Home',   path: '/' },
        { name: catName, path: `/#${product.category?.slug || 'new-arrivals'}` },
        { name,          path: `/product/${id}` },
      ]),
      organizationSchema(),
    ] : undefined,
  });

  if (loading) return <div style={{ display:'flex', alignItems:'center', justifyContent:'center', minHeight:'60vh' }}><div className="spinner" /></div>;
  if (!product) return <div style={{ textAlign:'center', padding:'4rem', color:'#555' }}>Product not found</div>;

  return (
    <div className="page-container" style={{ padding:'40px 24px', maxWidth:1100 }}>
      <div className="product-grid" style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:48, alignItems:'start' }}>

        {/* Image */}
        <div style={{ position:'relative' }}>
          <div style={{ background:'#111', borderRadius:20, overflow:'hidden', aspectRatio:'1', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'8rem', border:'1px solid rgba(255,255,255,.07)' }}>
            {product.images?.[0]
              ? <img src={product.images[0]} alt={name} style={{ width:'100%', height:'100%', objectFit:'cover' }} />
              : <span style={{ opacity:.15 }}>🌿</span>
            }
          </div>
          <button onClick={toggleWishlist} style={{ position:'absolute', top:16, right:16, background:'rgba(0,0,0,.7)', border:'1px solid rgba(255,255,255,.1)', borderRadius:'50%', width:42, height:42, display:'flex', alignItems:'center', justifyContent:'center', fontSize:'1.1rem', cursor:'pointer', color: inWishlist ? '#e05050' : '#888' }}>
            {inWishlist ? '❤️' : '♡'}
          </button>
          {product.badge && (
            <span className={`prod-card-badge badge-${product.badge}`} style={{ top:16, left:16 }}>
              {product.badge === 'hot' ? t('bestseller', lang) : product.badge === 'new' ? t('new_badge', lang) : t('sale', lang)}
            </span>
          )}
        </div>

        {/* Info */}
        <div>
          <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:'.72rem', fontWeight:700, letterSpacing:'.2em', textTransform:'uppercase', color:'#4cca74', marginBottom:6 }}>
            {product.category?.name?.[lang] || product.category?.name?.en}
          </div>
          <h1 style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:'clamp(1.4rem,3vw,2rem)', fontWeight:700, lineHeight:1.15, marginBottom:12 }}>{name}</h1>

          {Array.isArray(product.tags) && product.tags.length > 0 && (
            <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginBottom:16 }}>
              {product.tags.map((tag, i) => <span key={i} className="prod-tag">{tag}</span>)}
            </div>
          )}

          <div style={{ display:'flex', alignItems:'baseline', gap:10, marginBottom:6 }}>
            <span style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:'2.2rem', color:'#4cca74', letterSpacing:'.04em' }}>{fmt(product.price_eur)}</span>
            {product.price_eur_original > product.price_eur && (
              <span style={{ fontSize:'.95rem', color:'#555', textDecoration:'line-through' }}>{fmt(product.price_eur_original)}</span>
            )}
          </div>
          <p style={{ fontSize:'.75rem', color:'#444', marginBottom:20 }}>Charged as {formatEurCheckout(product.price_eur)} · EU legal collector's item</p>

          {desc && <p style={{ color:'#888', fontSize:'.92rem', lineHeight:1.9, fontWeight:300, marginBottom:24 }}>{desc}</p>}

          {/* Qty selector */}
          <div style={{ display:'flex', alignItems:'center', gap:16, marginBottom:24 }}>
            <div style={{ display:'flex', alignItems:'center', gap:10, background:'#111', borderRadius:25, padding:'8px 16px', border:'1px solid rgba(255,255,255,.07)' }}>
              <button onClick={() => setQty(q => Math.max(1,q-1))} style={{ background:'none', border:'none', color:'#888', fontSize:'1.2rem', cursor:'pointer', lineHeight:1, width:24 }}>−</button>
              <span style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:'1.3rem', minWidth:28, textAlign:'center' }}>{qty}</span>
              <button onClick={() => setQty(q => Math.min(product.stock||99,q+1))} disabled={qty>=(product.stock||99)} style={{ background:'none', border:'none', color: qty>=(product.stock||99) ? '#333' : '#4cca74', fontSize:'1.2rem', cursor:'pointer', lineHeight:1, width:24 }}>+</button>
            </div>
            <span style={{ fontSize:'.82rem', color: product.stock>0 ? '#4cca74' : '#e05050' }}>
              {product.stock > 0 ? `${product.stock} in stock` : t('out_of_stock', lang)}
            </span>
          </div>

          <div style={{ background:'rgba(240,165,0,.06)', border:'1px solid rgba(240,165,0,.12)', borderRadius:12, padding:'10px 16px', marginBottom:24, fontSize:'.78rem', color:'rgba(240,165,0,.75)', lineHeight:1.7 }}>
            ⚠️ Sold as a collector's item for display purposes only. Not for consumption. THC &lt;0.2% EU compliant.
          </div>

          <button className="btn-primary" style={{ width:'100%', fontSize:'1rem', padding:'15px 32px' }} onClick={handleAdd} disabled={product.stock < 1}>
            {added ? '✓ Added to Collection!' : product.stock < 1 ? t('out_of_stock', lang) : `${t('add_to_cart', lang)} — ${fmt(product.price_eur * qty)}`}
          </button>
        </div>
      </div>

      {/* Mobile layout override */}
      <style>{`
        @media (max-width: 768px) {
          .product-grid { grid-template-columns: 1fr !important; gap: 24px !important; }
        }
      `}</style>
    </div>
  );
}